import type { Locale, TranslateFn, TranslateParams } from "./types";
import { DEFAULT_LOCALE } from "./types";
import { vi, type Messages } from "./locales/vi";
import { en } from "./locales/en";

const catalogs: Record<Locale, Messages> = { vi, en };

/** Module-level locale for non-React code (validators, parsers). */
let activeLocale: Locale = DEFAULT_LOCALE;

export function getActiveLocale(): Locale {
  return activeLocale;
}

export function setActiveLocale(locale: Locale): void {
  activeLocale = locale;
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

function resolvePath(messages: Messages, key: string): string | undefined {
  const parts = key.split(".");
  let cur: unknown = messages;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    params[name] !== undefined ? String(params[name]) : `{${name}}`,
  );
}

export function createTranslator(locale: Locale): TranslateFn {
  const messages = catalogs[locale] ?? catalogs[DEFAULT_LOCALE];
  const fallback = catalogs[DEFAULT_LOCALE];

  return (key: string, params?: TranslateParams): string => {
    const raw =
      resolvePath(messages, key) ?? resolvePath(fallback, key) ?? key;
    return interpolate(raw, params);
  };
}

/** Translate using the currently active locale (for utils outside React). */
export function t(key: string, params?: TranslateParams): string {
  return createTranslator(activeLocale)(key, params);
}

export function getMessages(locale: Locale = activeLocale): Messages {
  return catalogs[locale] ?? catalogs[DEFAULT_LOCALE];
}
